import React, { useEffect, useState } from "react";
import { addClubCoach } from "../../services/coachService";
import { listMemberGenders } from "../../services/memberService";
import { checkRegistrationPaymentStatus } from "../../services/authService";
import { openPaymentGateway } from "../../services/cyberGatewayService";
import { API_BASE } from "../../services/api";

const PRIMARY = "#1d4ed8";
const NAVY = "#0f1c3f";
const LIGHT = "#e8f0fe";

const TITLE_OPTIONS = ["Mr", "Mrs", "Ms", "Miss", "Dr", "Rev."];
const ALLOWED_PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];

const emptyForm = () => ({
  title: "",
  initials: "",
  name_denoted_by_initials: "",
  lastname: "",
  member_gender_id: "",
  email: "",
  nic_number: "",
  primary_phone_number: "",
  secondary_phone_number: "",
  date_of_birth: "",
  address: "",
});

const Field = ({ label, required, error, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {error && <p className="text-xs text-red-500 mt-0.5">{error}</p>}
  </div>
);

const Input = ({ error, ...props }) => (
  <input
    {...props}
    className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 text-gray-700 placeholder-gray-400 ${
      error ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-blue-500"
    }`}
  />
);

const selectClass = (error) =>
  `w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 text-gray-700 ${
    error ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-blue-500"
  }`;

// "Add Coach" — the club's own primary coach adds one coach (new profile, or
// an existing coach account attached by email) via ClubTeamController::addCoach
// (see coachService.js/addClubCoach). Unlike RegisterAdditionalClubModal there
// is no preview step (the backend validates+creates in one call, no dry-run
// route exists for this endpoint), so the flow is just
// form -> submitted -> payment -> paid. Payment mirrors
// RegisterAdditionalClubModal's own mini payment step (mirroring
// BookingPaymentModal/IdCardReprintPaymentModal, NOT PaymentMethod.jsx, since
// that one hard-navigates to /payment-success which would yank an
// already-logged-in coach out of their dashboard) — addCoach already returns
// a real payment_payload, so card payment works exactly like registration.
//
// Props: `clubId`, `onClose()`, `onAdded()` — called once payment is
// confirmed "Paid" so the caller can refresh its coach list.
export default function AddCoachModal({ clubId, onClose, onAdded }) {
  // "new" | "existing"
  const [mode, setMode] = useState("new");
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [navError, setNavError] = useState("");
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoError, setPhotoError] = useState("");
  const [genders, setGenders] = useState([]);

  useEffect(() => {
    let active = true;
    listMemberGenders()
      .then((list) => active && setGenders(Array.isArray(list) ? list : []))
      .catch(() => active && setGenders([]));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => () => photoPreview && URL.revokeObjectURL(photoPreview), [photoPreview]);

  // phase: "form" -> "submitted" -> "payment" -> "paid"
  const [phase, setPhase] = useState("form");
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);

  const setField = (name) => (e) => {
    setForm((f) => ({ ...f, [name]: e.target.value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: "" } : prev));
  };

  const handlePhoto = (file) => {
    if (!file) return;
    const invalid = !ALLOWED_PHOTO_TYPES.includes(file.type)
      ? "Use a JPG, PNG, or WebP image"
      : file.size > 2 * 1024 * 1024
      ? "Max 2MB"
      : "";
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    if (invalid) {
      setPhoto(null);
      setPhotoPreview(null);
      setPhotoError(invalid);
      return;
    }
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
    setPhotoError("");
  };

  const validate = () => {
    const e = {};
    if (mode === "existing") {
      if (!form.email.trim() || !form.email.includes("@")) e.email = "Enter a valid email.";
      setErrors(e);
      return Object.keys(e).length === 0;
    }
    if (!form.title) e.title = "Required";
    if (!form.initials.trim()) e.initials = "Required";
    if (!form.name_denoted_by_initials.trim()) e.name_denoted_by_initials = "Required";
    if (!form.lastname.trim()) e.lastname = "Required";
    if (!form.member_gender_id) e.member_gender_id = "Required";
    if (!form.email.trim() || !form.email.includes("@")) e.email = "Enter a valid email.";
    if (!form.primary_phone_number.trim()) e.primary_phone_number = "Required";
    if (!form.address.trim()) e.address = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildFormData = () => {
    const fd = new FormData();
    fd.append("email", form.email.trim());
    if (mode === "new") {
      fd.append("title", form.title);
      fd.append("initials", form.initials.trim());
      fd.append("name_denoted_by_initials", form.name_denoted_by_initials.trim());
      fd.append("lastname", form.lastname.trim());
      fd.append("member_gender_id", form.member_gender_id);
      if (form.nic_number.trim()) fd.append("nic_number", form.nic_number.trim());
      fd.append("primary_phone_number", form.primary_phone_number.trim());
      if (form.secondary_phone_number.trim()) fd.append("secondary_phone_number", form.secondary_phone_number.trim());
      if (form.date_of_birth) fd.append("date_of_birth", form.date_of_birth);
      fd.append("address", form.address.trim());
      if (photo) fd.append("photo", photo);
    }
    return fd;
  };

  const applyBackendErrors = (backendErrors, fallback) => {
    const mapped = {};
    const general = [];
    Object.entries(backendErrors || {}).forEach(([key, msgs]) => {
      const msg = Array.isArray(msgs) ? msgs[0] : String(msgs);
      if (key === "photo") setPhotoError(msg);
      else if (Object.prototype.hasOwnProperty.call(form, key)) mapped[key] = msg;
      else general.push(msg);
    });
    setErrors((prev) => ({ ...prev, ...mapped }));
    setNavError(general[0] || fallback || "Please correct the highlighted fields.");
  };

  const handleSubmit = async () => {
    if (!validate()) {
      setNavError("Please correct the highlighted fields below.");
      return;
    }
    setNavError("");
    setSubmitting(true);
    try {
      const data = await addClubCoach(clubId, buildFormData());
      setCreated({
        process_id: data?.process_id,
        payable_amount: data?.payable_amount,
        payment_reference: data?.external_payment_reference || data?.payment_reference,
        payment_payload: data?.payment_payload,
        existing_member: Boolean(data?.existing_member),
        warning: data?.warning || null,
      });
      setPhase("submitted");
    } catch (err) {
      const data = err?.response?.data;
      if (data?.errors) applyBackendErrors(data.errors, data.message);
      else setNavError(data?.message || "Could not add this coach. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Payment step — mirrors RegisterAdditionalClubModal's own mini payment
  // flow (same popup+poll pattern as BookingPaymentModal/
  // IdCardReprintPaymentModal), just with process_type "member" since
  // addCoach creates a member_registration_process row. ──
  const [selectedMethod, setSelectedMethod] = useState("card");
  const [gatewayOpened, setGatewayOpened] = useState(0);
  const [paymentError, setPaymentError] = useState("");
  const [popup, setPopup] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [paymentNotice, setPaymentNotice] = useState("");
  const [counterConfirmed, setCounterConfirmed] = useState(false);

  const amount = created?.payable_amount != null ? Number(created.payable_amount) : null;
  const amountLabel = amount != null ? `LKR ${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "—";
  const voucherUrl =
    created?.process_id && created?.payment_reference
      ? `${API_BASE}/api/registration-payments/members/${created.process_id}/applicant-shroff-voucher?payment_reference=${encodeURIComponent(created.payment_reference)}`
      : null;

  const verifyPayment = async () => {
    if (!created?.process_id || !created?.payment_reference) return;
    setVerifying(true);
    setPaymentNotice("");
    try {
      const res = await checkRegistrationPaymentStatus({
        process_type: "member",
        process_id: created.process_id,
        payment_reference: created.payment_reference,
      });
      if (res?.payment_status === "Paid") {
        setPhase("paid");
        return;
      }
      setPaymentNotice(
        res?.payment_status
          ? `Payment status: ${res.payment_status}. If you completed the payment, please wait a moment and check again.`
          : "Payment is still pending. If you completed the payment, please wait a moment and check again."
      );
    } catch {
      setPaymentNotice("Could not verify payment status. Please check again.");
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    if (!gatewayOpened || !popup) return undefined;
    if (!created?.process_id || !created?.payment_reference) return undefined;

    let stopped = false;
    let inFlight = false;
    const stopPolling = () => {
      stopped = true;
      clearInterval(timer);
      window.removeEventListener("focus", onFocusOrVisible);
      document.removeEventListener("visibilitychange", onFocusOrVisible);
    };
    const tick = async () => {
      if (stopped || inFlight) return;
      if (popup.closed) {
        stopPolling();
        verifyPayment();
        return;
      }
      inFlight = true;
      try {
        const res = await checkRegistrationPaymentStatus({
          process_type: "member",
          process_id: created.process_id,
          payment_reference: created.payment_reference,
        });
        if (stopped) return;
        if (res?.payment_status === "Paid") {
          stopPolling();
          popup.close();
          setPhase("paid");
        }
      } catch {
        // Transient error — keep polling.
      } finally {
        inFlight = false;
      }
    };
    const timer = setInterval(tick, 3000);
    const onFocusOrVisible = () => {
      if (document.visibilityState === "hidden") return;
      tick();
    };
    window.addEventListener("focus", onFocusOrVisible);
    document.addEventListener("visibilitychange", onFocusOrVisible);
    return () => {
      stopped = true;
      clearInterval(timer);
      window.removeEventListener("focus", onFocusOrVisible);
      document.removeEventListener("visibilitychange", onFocusOrVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatewayOpened]);

  const handlePaymentContinue = () => {
    setPaymentError("");
    setPaymentNotice("");
    if (selectedMethod === "counter") {
      setCounterConfirmed(true);
      return;
    }
    if (!created?.payment_payload) {
      setPaymentError("Missing payment details. Please close and try again.");
      return;
    }
    const win = openPaymentGateway(created.payment_payload);
    if (!win) {
      setPopup(null);
      setPaymentError("Your browser blocked the payment window. Please allow popups for this site and try again.");
      return;
    }
    setPopup(win);
    setGatewayOpened((n) => n + 1);
  };

  const confirmingPayment = selectedMethod === "card" && gatewayOpened > 0 && !paymentNotice && phase === "payment";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto">
        {phase === "form" && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* ══ FORM ══ */}
        {phase === "form" && (
          <>
            <h2 className="text-lg font-bold pr-8" style={{ color: NAVY }}>Add Coach</h2>
            <p className="text-sm text-gray-500 mb-5">Add a new coach to your club, or attach an existing coach account by email.</p>

            <div className="flex gap-2 mb-5">
              {[
                ["new", "New Coach"],
                ["existing", "Existing Coach"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setMode(key);
                    setErrors({});
                    setNavError("");
                  }}
                  className={`flex-1 text-sm font-semibold py-2 rounded-lg border-2 transition ${
                    mode === key ? "border-blue-600 bg-blue-50/60 text-blue-700" : "border-gray-200 text-gray-500 hover:border-gray-300"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {mode === "existing" ? (
              <Field label="Coach's Email" required error={errors.email}>
                <Input type="email" value={form.email} onChange={setField("email")} placeholder="coach@example.com" error={errors.email} />
              </Field>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 rounded-full bg-gray-100 overflow-hidden flex-shrink-0 flex items-center justify-center">
                    {photoPreview ? (
                      <img src={photoPreview} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <svg className="w-6 h-6 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <input
                      id="add-coach-photo"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={(e) => handlePhoto(e.target.files?.[0])}
                    />
                    <label
                      htmlFor="add-coach-photo"
                      className="inline-block text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition cursor-pointer"
                    >
                      {photo ? "Change Photo" : "Upload Photo (optional)"}
                    </label>
                    {photoError && <p className="text-xs text-red-500 mt-1">{photoError}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Field label="Title" required error={errors.title}>
                    <select value={form.title} onChange={setField("title")} className={selectClass(errors.title)}>
                      <option value="">-- Select --</option>
                      {TITLE_OPTIONS.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Initials" required error={errors.initials}>
                    <Input value={form.initials} onChange={setField("initials")} placeholder="A.B." error={errors.initials} />
                  </Field>
                  <Field label="Name Denoted by Initials" required error={errors.name_denoted_by_initials}>
                    <Input value={form.name_denoted_by_initials} onChange={setField("name_denoted_by_initials")} placeholder="A.B. Perera" error={errors.name_denoted_by_initials} />
                  </Field>
                  <Field label="Last Name" required error={errors.lastname}>
                    <Input value={form.lastname} onChange={setField("lastname")} placeholder="Perera" error={errors.lastname} />
                  </Field>
                  <Field label="Gender" required error={errors.member_gender_id}>
                    <select value={form.member_gender_id} onChange={setField("member_gender_id")} className={selectClass(errors.member_gender_id)}>
                      <option value="">-- Select --</option>
                      {genders.map((g) => (
                        <option key={g.category_id} value={g.category_id}>{g.description}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Email" required error={errors.email}>
                    <Input type="email" value={form.email} onChange={setField("email")} placeholder="coach@example.com" error={errors.email} />
                  </Field>
                  <Field label="National ID" error={errors.nic_number}>
                    <Input value={form.nic_number} onChange={setField("nic_number")} placeholder="Optional" error={errors.nic_number} />
                  </Field>
                  <Field label="Primary Phone" required error={errors.primary_phone_number}>
                    <Input value={form.primary_phone_number} onChange={setField("primary_phone_number")} placeholder="077 123 4567" error={errors.primary_phone_number} />
                  </Field>
                  <Field label="Secondary Phone" error={errors.secondary_phone_number}>
                    <Input value={form.secondary_phone_number} onChange={setField("secondary_phone_number")} placeholder="Optional" error={errors.secondary_phone_number} />
                  </Field>
                  <Field label="Date of Birth" error={errors.date_of_birth}>
                    <input
                      type="date"
                      value={form.date_of_birth}
                      onChange={setField("date_of_birth")}
                      max={new Date().toLocaleDateString("en-CA")}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                    />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Address" required error={errors.address}>
                      <Input value={form.address} onChange={setField("address")} placeholder="123, Lake View Road, Nugegoda" error={errors.address} />
                    </Field>
                  </div>
                </div>
              </>
            )}

            {navError && <p className="text-sm text-red-500 mt-4">{navError}</p>}

            <div className="flex items-center justify-between mt-6">
              <button onClick={onClose} className="px-5 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition">
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-6 py-2.5 bg-blue-700 hover:bg-blue-800 disabled:bg-blue-300 text-white rounded-lg text-sm font-semibold transition"
              >
                {submitting ? "Submitting…" : "Add Coach"}
              </button>
            </div>
          </>
        )}

        {/* ══ SUBMITTED ══ */}
        {phase === "submitted" && (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-blue-50">
              <svg className="w-8 h-8 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Request Submitted!</h2>
            <p className="text-sm text-gray-500 mb-1 max-w-sm mx-auto">
              {created?.existing_member
                ? "Complete payment to add this existing coach to your club."
                : "Complete payment to add this coach to your club."}
            </p>
            {created?.warning && <p className="text-xs text-amber-600 mb-4 max-w-sm mx-auto">{created.warning}</p>}
            <button
              onClick={() => setPhase("payment")}
              className="w-full bg-blue-700 hover:bg-blue-800 text-white font-semibold py-3 rounded-lg text-sm mb-3 mt-4 transition"
            >
              Proceed to Payment
            </button>
            <button onClick={onClose} className="w-full text-sm text-gray-500 hover:text-blue-600">
              I'll Pay Later
            </button>
          </div>
        )}

        {/* ══ PAYMENT ══ */}
        {phase === "payment" && !counterConfirmed && !verifying && (
          <div className="max-w-md mx-auto">
            <h2 className="text-lg font-bold mb-1" style={{ color: NAVY }}>Complete Payment</h2>
            <p className="text-sm text-gray-500 mb-5">Amount due: <span className="font-semibold text-gray-700">{amountLabel}</span></p>

            {created?.payment_payload && (
              <label className={`relative flex items-start gap-3 rounded-xl border-2 px-4 py-3.5 cursor-pointer transition ${selectedMethod === "card" ? "border-blue-600 bg-blue-50/60" : "border-gray-200 hover:border-gray-300"}`}>
                <input type="radio" name="addCoachPaymentMethod" className="sr-only" checked={selectedMethod === "card"} onChange={() => setSelectedMethod("card")} />
                <span className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedMethod === "card" ? "border-blue-600" : "border-gray-300"}`}>
                  {selectedMethod === "card" && <span className="w-2.5 h-2.5 rounded-full bg-blue-600" />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="text-sm font-bold text-gray-900">Card Payment</span>
                  <span className="block text-xs text-gray-500 mt-0.5">Pay securely using your debit or credit card</span>
                </span>
              </label>
            )}

            <label className={`relative flex items-start gap-3 rounded-xl border-2 px-4 py-3.5 cursor-pointer transition mt-3 ${selectedMethod === "counter" ? "border-blue-600 bg-blue-50/60" : "border-gray-200 hover:border-gray-300"}`}>
              <input type="radio" name="addCoachPaymentMethod" className="sr-only" checked={selectedMethod === "counter"} onChange={() => setSelectedMethod("counter")} />
              <span className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedMethod === "counter" ? "border-blue-600" : "border-gray-300"}`}>
                {selectedMethod === "counter" && <span className="w-2.5 h-2.5 rounded-full bg-blue-600" />}
              </span>
              <span className="flex-1 min-w-0">
                <span className="text-sm font-bold text-gray-900">Pay at Counter</span>
                <span className="block text-xs text-gray-500 mt-0.5">Pay at the club office or administration counter</span>
              </span>
            </label>

            {paymentError && <p className="text-sm text-red-500 text-center mt-4">{paymentError}</p>}
            {paymentNotice && <p className="text-sm text-amber-600 text-center mt-4">{paymentNotice}</p>}

            <div className="border-t border-gray-100 mt-5 pt-5">
              <button
                onClick={handlePaymentContinue}
                disabled={confirmingPayment}
                className="w-full text-white font-semibold py-2.5 rounded-xl text-sm transition disabled:opacity-70"
                style={{ backgroundColor: PRIMARY }}
              >
                {confirmingPayment ? "Confirming Payment…" : selectedMethod === "card" && gatewayOpened ? "Reopen Payment Window" : "Proceed to Pay"}
              </button>
              {confirmingPayment && <p className="text-xs font-medium text-amber-600 text-center mt-3">Confirming your payment... please don't close this.</p>}
              {paymentNotice && (
                <button onClick={verifyPayment} className="w-full text-sm font-medium border rounded-xl py-2 mt-3 transition" style={{ color: PRIMARY, borderColor: LIGHT }}>
                  Check Payment Status Again
                </button>
              )}
              <button onClick={onClose} className="w-full text-sm font-medium text-gray-500 hover:text-gray-700 border border-gray-200 hover:bg-gray-50 rounded-xl py-2.5 mt-3 transition">
                Close
              </button>
            </div>
          </div>
        )}

        {phase === "payment" && verifying && (
          <div className="flex flex-col items-center gap-4 text-center py-10">
            <svg className="animate-spin w-10 h-10" fill="none" viewBox="0 0 24 24" stroke={PRIMARY}>
              <circle className="opacity-25" cx="12" cy="12" r="10" strokeWidth="4" />
              <path className="opacity-75" strokeWidth="4" strokeLinecap="round" d="M4 12a8 8 0 018-8" />
            </svg>
            <p className="text-base font-semibold" style={{ color: NAVY }}>Verifying your payment…</p>
          </div>
        )}

        {phase === "payment" && counterConfirmed && (
          <div className="max-w-sm mx-auto text-center py-4">
            <div className="mx-auto w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mb-3">
              <svg className="w-7 h-7 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2M5 21H3m6-14h.01M9 11h.01M9 15h.01M14 7h.01M14 11h.01M14 15h.01" />
              </svg>
            </div>
            <h2 className="text-lg font-bold" style={{ color: NAVY }}>Pay at Counter</h2>
            <p className="text-sm text-gray-500 mt-1">Take this reference to the club office / administration counter.</p>
            <div className="rounded-xl px-4 py-4 mt-4" style={{ backgroundColor: LIGHT }}>
              <p className="text-xs text-gray-500">Reference Number</p>
              <p className="text-xl font-extrabold mt-1 font-mono break-all" style={{ color: PRIMARY }}>{created?.payment_reference || "—"}</p>
              <p className="text-xs text-gray-500 mt-2">Amount due: <span className="font-semibold text-gray-700">{amountLabel}</span></p>
            </div>
            {voucherUrl && (
              <a href={voucherUrl} target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 border-2 font-semibold py-2.5 rounded-xl text-sm mt-4 transition" style={{ borderColor: PRIMARY, color: PRIMARY }}>
                Download Voucher
              </a>
            )}
            <button onClick={() => setCounterConfirmed(false)} className="w-full text-sm font-medium text-gray-500 hover:text-gray-700 border border-gray-200 hover:bg-gray-50 rounded-xl py-2.5 mt-3 transition">
              Back
            </button>
            <button onClick={onClose} className="w-full px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition mt-3" style={{ backgroundColor: PRIMARY }}>
              Done — I'll Pay Later
            </button>
          </div>
        )}

        {/* ══ PAID ══ */}
        {phase === "paid" && (
          <div className="max-w-sm mx-auto text-center py-4">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
              <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-lg font-bold" style={{ color: NAVY }}>Payment Confirmed</h2>
            <p className="text-sm text-gray-500 mt-1">The coach has been added to your club.</p>
            <button
              onClick={() => {
                onAdded?.();
                onClose();
              }}
              className="w-full mt-6 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition"
              style={{ backgroundColor: PRIMARY }}
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
